import { isIn } from '@/api/helpers';
import { Course, ListCoursesResponse, MaterialType } from '@/client/courses';
import { useCourses } from '@/hooks/query/courses.hooks';
import {
  COURSES_LIST_CACHE_TIME,
  LISTING_REQUEST_MAX_TAKE,
} from '@/hooks/query/courses.query-keys';
import { NEW_COURSE_MONTHS } from '@/utils/helpers/courses.helper';
import moment from 'moment';
import { useMemo } from 'react';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const BROWSE_CONTENT_TYPES = [
  MaterialType.COURSE,
  MaterialType.CUSTOM_MATERIAL,
];

export type BrowseViewCoursesRow = {
  courses: Course[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isPreviousData: boolean;
  isError: boolean;
  error: unknown;
  isTruncated: boolean;
  refetch: UseQueryResult<ListCoursesResponse>['refetch'];
};

export type BrowseViewCourses = {
  cyberPilotContent: BrowseViewCoursesRow;
  newAndUpcoming: BrowseViewCoursesRow;
  customContent: BrowseViewCoursesRow;
};

const compareNullableLast = <T>(
  a: T | null | undefined,
  b: T | null | undefined,
  compare: (first: T, second: T) => number,
): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  return compare(a, b);
};

const byDate = (a: string | Date, b: string | Date) =>
  new Date(a).getTime() - new Date(b).getTime();

type BrowseRowContext = { releasedFromDate: Date };

type BrowseRowDefinition = {
  key: keyof BrowseViewCourses;
  matches: (course: Course, context: BrowseRowContext) => boolean;
  compare: (a: Course, b: Course) => number;
};

const BROWSE_ROW_DEFINITIONS: BrowseRowDefinition[] = [
  {
    key: 'cyberPilotContent',
    matches: (course) => !!course.isStandardCatalogue,
    compare: (a, b) =>
      compareNullableLast(
        a.totalNumberOfEnrollments,
        b.totalNumberOfEnrollments,
        (first, second) => first - second,
      ) ||
      compareNullableLast(
        a.priority,
        b.priority,
        (first, second) => first - second,
      ),
  },
  {
    key: 'newAndUpcoming',
    matches: (course, { releasedFromDate }) =>
      !!course.releaseDate &&
      new Date(course.releaseDate).getTime() >= releasedFromDate.getTime(),
    compare: (a, b) =>
      compareNullableLast(a.releaseDate, b.releaseDate, byDate),
  },
  {
    key: 'customContent',
    matches: (course) => !!course.account,
    compare: (a, b) =>
      compareNullableLast(a.releaseDate, b.releaseDate, (first, second) =>
        byDate(second, first),
      ),
  },
];

const toBrowseViewCoursesRow = (
  row: ReturnType<typeof useCourses>,
  courses: Course[] | undefined,
): BrowseViewCoursesRow => ({
  courses,
  isLoading: row.isLoading,
  isFetching: row.isFetching,
  isPreviousData: row.isPreviousData,
  isError: row.isError,
  error: row.error,
  isTruncated: !!row.courses && row.courses.count > row.courses.result.length,
  refetch: row.refetch,
});

export const useBrowseViewCourses = (
  params: UseQueryOptions & {
    accountId?: string;
  } = {},
): BrowseViewCourses => {
  const {
    accountId,
    cacheTime = COURSES_LIST_CACHE_TIME,
    ...restOptions
  } = params;

  const releasedFromDate = useMemo(
    () =>
      moment().subtract(NEW_COURSE_MONTHS, 'months').startOf('day').toDate(),
    [],
  );

  const allCourses = useCourses({
    ...restOptions,
    accountId,
    cacheTime,
    take: LISTING_REQUEST_MAX_TAKE,
    filters: [isIn('type', BROWSE_CONTENT_TYPES)],
    includeEnrollmentStatistics: 1,
  });

  const result = allCourses.courses?.result;

  const partitions = useMemo(() => {
    if (!result) return undefined;

    return BROWSE_ROW_DEFINITIONS.reduce((acc, definition) => {
      acc[definition.key] = result
        .filter((course) => definition.matches(course, { releasedFromDate }))
        .sort(definition.compare);

      return acc;
    }, {} as Record<keyof BrowseViewCourses, Course[]>);
  }, [result, releasedFromDate]);

  return BROWSE_ROW_DEFINITIONS.reduce((acc, definition) => {
    acc[definition.key] = toBrowseViewCoursesRow(
      allCourses,
      partitions?.[definition.key],
    );

    return acc;
  }, {} as BrowseViewCourses);
};
